import { Icon } from '@iconify/react'
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Tooltip,
  Chip,
  Spinner,
  useDisclosure,
} from '@nextui-org/react'

import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import { SubscribeLimitDocument } from 'src/components/modals/subscribe-limit-document'
import { useSocketContextData } from 'src/context/socket'
import { useSubscriptionContext } from 'src/context/subscription'
import { useWorkspaceContextData } from 'src/context/workspace'
import api, { baseURL } from 'src/lib/api'
import Mixpanel from 'src/lib/mixpanel'
import { PowerDocsEventsName } from 'src/types/SocketEvents'

interface IDocumentListProps {
  doc: any
  isProcessing?: boolean
  setDoc: (value: any) => void
}
const documentStates = [
  { key: 'DRAFT', text: 'Draft', icon: <Icon icon="lucide:pencil-line" width="15" /> },
  { key: 'PENDING_APPROVAL', text: 'Pending approval', icon: <Icon icon="lucide:hourglass" width="15" /> },
  { key: 'APPROVED', text: 'Approved', icon: <Icon icon="lucide:check-circle" width="15" /> },
  { key: 'SENT', text: 'Sent', icon: <Icon icon="lucide:send" width="15" /> },
  { key: 'SIGNED', text: 'Signed', icon: <Icon icon="lucide:signature" width="15" /> },
  { key: 'REJECTED', text: 'Rejected', icon: <Icon icon="lucide:x-circle" width="15" /> },
  { key: 'ARCHIVED', text: 'Archived', icon: <Icon icon="lucide:archive" width="15" /> },
]
const variants = ['Proposal', 'Quotation', 'Offer']
export default function DocumentList({ doc, setDoc, isProcessing }: IDocumentListProps) {
  const [isGenerating, setIsGenerating] = useState<boolean>(false)
  const { activeWorkspace } = useWorkspaceContextData()
  const { io } = useSocketContextData()

  const updateStatus = (status: string, variant: string, index?: number) => {
    toast.promise(
      () =>
        io.emitWithAck(PowerDocsEventsName.UPDATE_POWERDOC_STATUS, {
          status,
          documentUUID: variant === 'custom' ? doc.customDocs?.[index]?.uuid : doc.uuid,
          token: localStorage.getItem('accessToken'),
          variant: variant.toUpperCase(),
        }),
      {
        loading: `Updating ${variant.toLowerCase()} status`,
        success: 'Status has been updated',
      },
    )
    setDoc((prev) => {
      const updatedDoc = { ...prev }
      if (variant === 'custom' && index !== undefined) {
        updatedDoc.customDocs[index].status = status
      } else {
        updatedDoc[variant.toLowerCase()].status = status
      }
      return updatedDoc
    })
  }
  const download = (variant: string, index?: number) => {
    console.log(variant, index, doc)
    const documentUUID =
      variant.toLowerCase() === 'custom' && index !== undefined ? doc.customDocs?.[index]?.uuid : doc.uuid
    const popup = window.open(`${baseURL}/powerdocs/export/${documentUUID}/${variant}`)
    if (popup) {
      popup?.focus()
      /*popup.addEventListener('unload', () => {
        popup.close()
      })*/
    }
  }
  const share = (variant: string, index?: number) => {
    try {
      const documentUUID =
        variant.toLowerCase() === 'custom' && index !== undefined ? doc.customDocs?.[index]?.uuid : doc.uuid
      const url = `${document.location.origin}/${variant.toLowerCase()}/${documentUUID}`
      navigator.clipboard.writeText(url)
      toast.success('Link copied to clipboard')

      Mixpanel.track('Share link copied', {
        location: 'Project Overview',
        document_id: doc.uuid,
        link: url,
        variant,
      })
    } catch (error) {
      toast.error('Failed to copy the link. Please try again.')
    }
  }
  const { profile } = useWorkspaceContextData()
  const { getUserSubscription, currentLimitations } = useSubscriptionContext()
  const {
    isOpen: isSubscriptionModalOpen,
    onOpen: openSubscriptionModal,
    onClose: closeSubscriptionModal,
  } = useDisclosure()

  const generateSubDoc = async (variant: string, subType: string) => {
    if (currentLimitations) {
      if (currentLimitations.isFreeTrial || (currentLimitations?.hasSubscription && currentLimitations.currentSubscription === 'EXECUTE')) {
        if (currentLimitations.currentSubscription === 'EXECUTE') {
          toast.loading('Generating ' + variant.toLowerCase() + '...')
          Mixpanel.track('Generate Quotation clicked', {
            document_id: doc.uuid,
            location: 'Doc Switcher',
          })
          setIsGenerating(true)
          io.emitWithAck(PowerDocsEventsName.GENERATE_SUBDOCUMENT, {
            token: localStorage.getItem('accessToken'),
            workspaceUUID: activeWorkspace,
            powerDocUUID: doc.uuid,
            variant,
            subType,
          })
        }else{
          openSubscriptionModal()
        }
      } else {
        openSubscriptionModal()
      }
    }
    
  }

  const generateQuotation = async () => {
    toast.loading('Generating quotation...')
    Mixpanel.track('Generate Quotation clicked', {
      document_id: doc.uuid,
      location: 'Doc Switcher',
    })
    setIsGenerating(true)
    io.emitWithAck(PowerDocsEventsName.GENERATE_QUOTATION, {
      token: localStorage.getItem('accessToken'),
      workspaceUUID: activeWorkspace,
      powerDocUUID: doc.uuid,
    })
  }
  const generateProposal = async () => {
    toast.loading('Generating proposal...')
    Mixpanel.track('Generate Proposal clicked', {
      document_id: doc.uuid,
      location: 'Doc Switcher',
    })
    setIsGenerating(true)
    io.emitWithAck(PowerDocsEventsName.GENERATE_PROPOSAL, {
      token: localStorage.getItem('accessToken'),
      workspaceUUID: activeWorkspace,
      powerDocUUID: doc.uuid,
    })
  }
  const generateOffer = async () => {
    toast.loading('Generating offer...')
    Mixpanel.track('Generate Offer clicked', {
      document_id: doc.uuid,
      location: 'Sections Tables',
    })
    setIsGenerating(true)
    io.emitWithAck(PowerDocsEventsName.GENERATE_OFFER, {
      token: localStorage.getItem('accessToken'),
      workspaceUUID: activeWorkspace,
      powerDocUUID: doc.uuid,
    })
  }
  const navigate = useNavigate()
  const params = useParams()
  useEffect(() => {
    if (io) {
      io.on(PowerDocsEventsName.QUOTATION_GENERATED, () => {
        setIsGenerating(false)
        toast.dismiss()
        navigate(`/client/${params.clientUUID}/project/${doc.uuid}/quotation`)
      })
      io.on(PowerDocsEventsName.OFFER_GENERATED, () => {
        setIsGenerating(false)
        toast.dismiss()
        navigate(`/client/${params.clientUUID}/project/${doc.uuid}/offer`)
      })
      io.on(PowerDocsEventsName.PROPOSAL_GENERATED, () => {
        setIsGenerating(false)
        toast.dismiss()
        navigate(`/client/${params.clientUUID}/project/${doc.uuid}/proposal`)
      })
    }
    if (io && doc?.uuid) {
      io.emit('join_channel', { channelName: doc?.uuid })
    }
  }, [io, doc])

  const deleteDocument = async (variant: string, index?: number) => {
    const documentUUID =
      variant.toLowerCase() === 'custom' && index !== undefined ? doc.customDocs?.[index]?.uuid : doc.uuid
    await toast.promise(api.delete(`/powerdocs/${documentUUID}/${variant.toLowerCase()}`), {
      loading: `Removing ${variant} document...`,
      success: `${variant} has been removed`,
      error: `Error removing ${variant} document, please try again`,
    })
    setDoc((prev: any) => {
      const updatedDoc = { ...prev }
      if (variant === 'custom' && index !== undefined) {
        updatedDoc.customDocs = updatedDoc.customDocs?.filter((_: any, i: number) => i !== index)
      } else {
        updatedDoc[variant.toLowerCase()] = null
      }
      return updatedDoc
    })
  }

  const documentsTypes = useMemo(
    () => [
      {
        title: 'Sales Proposal',
        description: 'Convince a client to purchase a product or service.',
        documentType: 'PROPOSAL',
        subType: 'Sales Proposal',
        available: true,
        icon: 'lucide:file-text',
      },
      {
        title: 'Tender Proposal',
        description: 'Bid for public contracts or services by responding to an RFP.',
        documentType: 'PROPOSAL',
        subType: 'Tender Proposal',
        available: true,
        icon: 'lucide:file-text',
      },
      {
        title: 'Project Proposal',
        description: 'Outline a plan to execute a specific project for approval.',
        documentType: 'PROPOSAL',
        subType: 'Project Proposal',
        available: true,
        icon: 'lucide:file-text',
      },
      {
        title: 'Offer',
        description: 'A formal proposal outlining the services, terms, & pricing, which becomes binding once accepted.',
        documentType: 'OFFER',
        subType: '',
        available: true,
        icon: 'lucide:file-text',
      },
      {
        title: 'Quotation',
        description: 'A formal statement outlining the price, terms, and conditions of a proposed offer or contract.',
        documentType: 'QUOTATION',
        subType: '',
        available: true,
        icon: 'lucide:file-text',
      },
      {
        title: 'Contract',
        description: 'Legally binding document detailing obligations, responsibilities, and terms.',
        documentType: 'CONTRACT',
        subType: '',
        available: false,
        icon: 'lucide:file-text',
      },
      {
        title: 'NDA (Non-Disclosure Agreement)',
        description: 'A Contract protecting confidential information.',
        documentType: 'NDA',
        subType: '',
        available: false,
        icon: 'lucide:file-text',
      },
      {
        title: 'Invoice',
        description: 'A request for payment after work is completed.',
        documentType: 'INVOICE',
        subType: '',
        available: false,
        icon: 'lucide:file-text',
      },
    ],
    [],
  )
  const handleAction = (action: string) => {
    if (action.startsWith('download_')) {
      if (action === 'download_proposal') {
        download('proposal')
      } else if (action === 'download_offer') {
        download('offer')
      } else if (action === 'download_quotation') {
        download('quotation')
      } else if (action.startsWith('download_custom_')) {
        const index = parseInt(action.split('_')[2], 10)
        download('custom', index)
      }
    } else if (action.startsWith('share_')) {
      if (action === 'share_proposal') {
        share('proposal')
      } else if (action === 'share_offer') {
        share('offer')
      } else if (action === 'share_quotation') {
        share('quotation')
      } else if (action.startsWith('share_custom_')) {
        const index = parseInt(action.split('_')[2], 10)
        share('custom', index)
      }
    } else if (action.startsWith('delete_')) {
      if (action === 'delete_proposal') {
        deleteDocument('proposal')
      } else if (action === 'delete_offer') {
        deleteDocument('offer')
      } else if (action === 'delete_quotation') {
        deleteDocument('quotation')
      } else if (action.startsWith('delete_custom_')) {
        const index = parseInt(action.split('_')[2], 10)
        deleteDocument('custom', index)
      }
    }
    switch (action) {
      case 'generate_proposal':
        generateProposal()
        break
      case 'generate_quotation':
        generateQuotation()
        break
      case 'generate_offer':
        generateOffer()
        break
    }
  }
  const isEmpty =
    (variants.map((variant) => doc?.[variant.toLowerCase()]).filter((item) => item).length === 0 &&
      doc?.customDocs?.length === 0) ||
    isProcessing
  return (
    <div className="pt-4">
      <Table
        classNames={{
          wrapper: 'p-0 shadow-none',
        }}
      >
        <TableHeader>
          <TableColumn className={'text-black bg-[#C3E3E8]'} width={'30%'}>
            Document
          </TableColumn>
          <TableColumn className={'text-black bg-[#C3E3E8]'} width={'10%'}>
            Status
          </TableColumn>
          <TableColumn className={'text-black bg-[#C3E3E8]'} width={'10%'}>
            Creation date
          </TableColumn>
          <TableColumn className={'text-black bg-[#C3E3E8]'} width={'10%'}>
            Last edit
          </TableColumn>
          <TableColumn className={'text-black bg-[#C3E3E8]'} width={'10%'}>
            Actions
          </TableColumn>
          <TableColumn className={'text-black bg-[#C3E3E8]'} width={'5%'}>
            More
          </TableColumn>
        </TableHeader>
        {!isEmpty ? (
          <TableBody>
            {variants.map((variant) =>
              doc[variant.toLowerCase()] ? (
                <TableRow
                  key={variant}
                  onClick={() => navigate(`/client/${params.clientUUID}/project/${doc.uuid}/${variant.toLowerCase()}`)}
                  className="cursor-pointer"
                >
                  <TableCell>
                    <span className="flex items-center gap-1 font-semibold">
                      {variant.toLowerCase() === 'proposal' && <Icon icon="lucide:file-text" width="18" />}
                      {variant.toLowerCase() === 'quotation' && <Icon icon="lucide:file-check" width="18" />}
                      {variant.toLowerCase() === 'offer' && <Icon icon="lucide:file-spreadsheet" width="18" />}
                      {variant}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Dropdown>
                      <DropdownTrigger>
                        <Button
                          variant="ghost"
                          className="focus:none data-[state=open]:bg-muted text-muted-foreground flex h-6 px-2 py-3 text-xs rounded-xl border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground"
                          size="sm"
                        >
                          <>
                            {documentStates.find((item) => item.key === doc?.[variant.toLowerCase()]?.status)?.icon}
                            <span className="ml-1">
                              {documentStates.find((item) => item.key === doc?.[variant.toLowerCase()]?.status)?.text ??
                                'Draft'}
                            </span>
                          </>
                        </Button>
                      </DropdownTrigger>
                      <DropdownMenu aria-label="Actions" onAction={(key: string) => updateStatus(key, variant)}>
                        {documentStates.map((item) => (
                          <DropdownItem key={item.key}>
                            <span className="flex items-center gap-2">
                              {item.icon}
                              {item.text}
                            </span>
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </Dropdown>
                  </TableCell>
                  <TableCell>
                    <span>{dayjs(doc?.[variant.toLowerCase()]?.createdAt).format('DD/MM/YYYY')}</span>
                  </TableCell>
                  <TableCell>
                    <span>{dayjs(doc?.[variant.toLowerCase()]?.updatedAt).format('DD/MM/YYYY - HH:mm')}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-0">
                      <Tooltip content="Edit" color="foreground" placement="bottom">
                        <Button
                          isIconOnly
                          variant="light"
                          size="md"
                          onPress={() =>
                            navigate(`/client/${params.clientUUID}/project/${doc.uuid}/${variant.toLowerCase()}`)
                          }
                        >
                          <Icon icon="lucide:file-pen" width="18" className="text-secondary" />
                        </Button>
                      </Tooltip>
                      <Tooltip content="Share" color="foreground" placement="bottom">
                        <Button isIconOnly variant="light" size="md" onPress={() => share(variant)}>
                          <Icon icon="lucide:send" width="18" className="text-secondary" />
                        </Button>
                      </Tooltip>
                      <Tooltip content="Download" color="foreground" placement="bottom">
                        <Button isIconOnly variant="light" size="md" onPress={() => download(variant)}>
                          <Icon icon="lucide:download" width="18" className="text-secondary" />
                        </Button>
                      </Tooltip>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Dropdown
                      classNames={{
                        content: 'rounded-xl border border-1 border-success-500 min-w-[150p]',
                      }}
                    >
                      <DropdownTrigger>
                        <Button
                          variant="light"
                          className="focus:none data-[state=open]:bg-muted text-muted-foreground flex size-8"
                          size="md"
                        >
                          <Icon icon={'lucide:more-horizontal'} />
                        </Button>
                      </DropdownTrigger>
                      <DropdownMenu aria-label="More" onAction={(key) => handleAction(key)}>
                        <DropdownItem key={`download_${variant.toLowerCase()}`}>
                          <span className="flex flex-row gap-2 items-center font-bold">
                            <Icon icon={'lucide:download'} color="#38A1B1" />
                            Download
                          </span>
                        </DropdownItem>
                        <DropdownItem
                          key={`share_${variant.toLowerCase()}`}
                          className="border-b-1 rounded-none border-b-success-500 border-b-opacity-80"
                        >
                          <span className="flex flex-row gap-2 items-center font-bold">
                            <Icon icon={'lucide:send'} color="#38A1B1" />
                            Share
                          </span>
                        </DropdownItem>
                        <DropdownItem key={`delete_${variant.toLowerCase()}`} className="text-danger" color="danger">
                          <span className="flex flex-row w-full font-bold items-center gap-2">
                            <Icon icon={'lucide:trash-2'} />
                            <span className={'text-md font-bold'}>Delete</span>
                          </span>
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  </TableCell>
                </TableRow>
              ) : (
                <></>
              ),
            )}
            {doc.customDocs?.map((customDoc: any, index: number) => (
              <>
                <TableRow
                  key={`custom_doc_${index}`}
                  onClick={() => navigate(`/client/${params.clientUUID}/project/${doc.uuid}/custom/${customDoc.uuid}`)}
                  className="cursor-pointer"
                >
                  <TableCell>
                    <span className="flex items-center gap-1 font-semibold">
                      <Icon icon="lucide:file-spreadsheet" width="18" /> {customDoc?.documentType}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Dropdown>
                      <DropdownTrigger>
                        <Button
                          variant="ghost"
                          className="focus:none data-[state=open]:bg-muted text-muted-foreground flex h-6 px-2 py-3 text-xs rounded-xl border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground"
                          size="sm"
                        >
                          <>
                            {documentStates.find((item) => item.key === customDoc?.status)?.icon}
                            <span className="ml-1">
                              {documentStates.find((item) => item.key === customDoc?.status)?.text ?? 'Draft'}
                            </span>
                          </>
                        </Button>
                      </DropdownTrigger>
                      <DropdownMenu aria-label="Actions" onAction={(key: string) => updateStatus(key, 'custom', index)}>
                        {documentStates.map((item) => (
                          <DropdownItem key={item.key}>
                            <span className="flex items-center gap-2">
                              {item.icon}
                              {item.text}
                            </span>
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </Dropdown>
                  </TableCell>
                  <TableCell>
                    <span>{dayjs(customDoc?.createdAt).format('DD/MM/YYYY')}</span>
                  </TableCell>
                  <TableCell>
                    <span>{dayjs(customDoc?.updatedAt).format('DD/MM/YYYY - HH:mm')}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-0">
                      <Tooltip content="Edit" color="foreground" placement="bottom">
                        <Button
                          isIconOnly
                          variant="light"
                          size="md"
                          onPress={() =>
                            navigate(`/client/${params.clientUUID}/project/${doc.uuid}/custom/${customDoc.uuid}`)
                          }
                        >
                          <Icon icon="lucide:file-pen" width="18" className="text-secondary" />
                        </Button>
                      </Tooltip>
                      <Tooltip content="Share" color="foreground" placement="bottom">
                        <Button isIconOnly variant="light" size="md" onPress={() => share('custom', index)}>
                          <Icon icon="lucide:send" width="18" className="text-secondary" />
                        </Button>
                      </Tooltip>
                      <Tooltip content="Download" color="foreground" placement="bottom">
                        <Button isIconOnly variant="light" size="md" onPress={() => download('custom', index)}>
                          <Icon icon="lucide:download" width="18" className="text-secondary" />
                        </Button>
                      </Tooltip>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Dropdown
                      classNames={{
                        content: 'rounded-xl border border-1 border-success-500 min-w-[150p]',
                      }}
                    >
                      <DropdownTrigger>
                        <Button
                          variant="light"
                          className="focus:none data-[state=open]:bg-muted text-muted-foreground flex size-8"
                          size="md"
                        >
                          <Icon icon={'lucide:more-horizontal'} />
                        </Button>
                      </DropdownTrigger>
                      <DropdownMenu aria-label="More" onAction={(key) => handleAction(key)}>
                        <DropdownItem key={`download_custom_${index}`}>
                          <span className="flex flex-row gap-2 items-center font-bold">
                            <Icon icon={'lucide:download'} color="#38A1B1" />
                            Download
                          </span>
                        </DropdownItem>
                        <DropdownItem
                          key={`share_custom_${index}`}
                          className="border-b-1 rounded-none border-b-success-500 border-b-opacity-80"
                        >
                          <span className="flex flex-row gap-2 items-center font-bold">
                            <Icon icon={'lucide:send'} color="#38A1B1" />
                            Share
                          </span>
                        </DropdownItem>
                        <DropdownItem key={`delete_custom_${index}`} className="text-danger" color="danger">
                          <span className="flex flex-row w-full font-bold items-center gap-2">
                            <Icon icon={'lucide:trash-2'} />
                            <span className={'text-md font-bold'}>Delete</span>
                          </span>
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  </TableCell>
                </TableRow>
              </>
            ))}
          </TableBody>
        ) : (
          <TableBody></TableBody>
        )}
      </Table>
      {isEmpty && (
        <div className="p-4 flex flex-col items-center mt-2">
          {profile.serviceTier === 'SELF_SERVE' ? (
            <>
              <Icon icon="lucide:file-question" fontSize={48} className="text-default-400" />
              <span className="text-xl font-medium text-default-400">No document is created yet</span>
              <span className="text-xl font-medium text-default-400">
                Start by creating the right document for your project
              </span>
              <div className="flex flex-col gap-8 p-2 mt-2">
                <div className="grid grid-cols-4 gap-4">
                  {documentsTypes.map((documentOption) => (
                    <div
                      key={documentOption.title}
                      className={`flex flex-col gap-3 border-3 p-4 py-8 rounded-lg ${
                        documentOption.available && !isGenerating ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                      } border-default-200`}
                      onClick={() =>
                        documentOption.available && !isGenerating
                          ? generateSubDoc(documentOption.documentType, documentOption.subType)
                          : ''
                      }
                    >
                      {documentOption.available ? (
                        <div className="border rounded-xl bg-default-100 border-none p-3 w-fit">
                          <Icon icon={documentOption.icon} width="20" />
                        </div>
                      ) : (
                        <div className="flex justify-between">
                          <div className="border rounded-xl bg-default-100 border-none p-3 w-fit">
                            <Icon icon="lucide:file-lock" width="20" />
                          </div>
                          <Chip color="secondary">Coming soon</Chip>
                        </div>
                      )}
                      <div>
                        <h3 className="text-lg font-semibold">{documentOption.title}</h3>
                        <p className="text-sm text-default-500">{documentOption.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <Spinner size="lg" />
              <span className="text-xl font-medium text-default-400">Your document is being generated</span>
              <span className="text-xl font-medium text-default-400">
                You will receive an email notification once it&apos;s ready
              </span>
            </>
          )}
        </div>
      )}
      <SubscribeLimitDocument isOpen={isSubscriptionModalOpen} onClose={closeSubscriptionModal} />
    </div>
  )
}
